/**
 * @module ToastProvider
 * @description A global toast notification provider using Sonner.
 * Provides consistent styling and positioning for all toast notifications in the application.
 * 
 * @example
 * ```jsx
 * // In your root component
 * function App() {
 *   return (
 *     <ToastProvider>
 *       <YourApp />
 *     </ToastProvider>
 *   );
 * }
 * 
 * // Using toasts anywhere in your app
 * import { toast } from 'sonner';
 * 
 * // Success toast
 * toast.success('Operation completed successfully');
 * 
 * // Error toast
 * toast.error('Something went wrong');
 * 
 * // Info toast with custom styling
 * toast.info('New message received', {
 *   style: { background: 'rgba(37, 99, 235, 0.1)' }
 * });
 * ```
 */

import { Toaster } from 'sonner';

/**
 * Global toast configuration
 * @type {Object}
 */
const toastOptions = {
  position: "top-right",
  richColors: true,
  expand: false,
  closeButton: true,
  visibleToasts: 3,
  offset: 8,
  toastOptions: {
    style: {
      background: 'rgba(30, 27, 75, 0.9)', // secondary (deep blue)
      color: '#F3F4F6', // gray-100
      border: '1px solid rgba(124, 58, 237, 0.3)', // primary (purple) border
      borderRadius: '0.375rem', // md
      backdropFilter: 'blur(8px)',
    },
    success: {
      style: {
        background: 'rgba(6, 182, 212, 0.15)', // cyan-500 with opacity
        border: '1px solid rgb(6, 182, 212)', // cyan-500
        color: '#A5F3FC', // cyan-200
      },
      iconTheme: {
        primary: '#06B6D4',
        secondary: '#CFFAFE',
      },
    },
    error: {
      style: {
        background: 'rgba(236, 72, 153, 0.15)', // pink-500 with opacity
        border: '1px solid rgb(236, 72, 153)', // pink-500
        color: '#FBCFE8', // pink-200
      },
      iconTheme: {
        primary: '#EC4899',
        secondary: '#FCE7F3',
      },
    },
    warning: {
      style: {
        background: 'rgba(245, 158, 11, 0.15)', // amber-500 with opacity
        border: '1px solid rgb(245, 158, 11)', // amber-500
        color: '#FDE68A', // amber-200
      },
      iconTheme: {
        primary: '#F59E0B',
        secondary: '#FEF3C7',
      },
    },
    info: {
      style: {
        background: 'rgba(124, 58, 237, 0.15)', // purple-600 with opacity
        border: '1px solid rgb(124, 58, 237)', // purple-600
        color: '#DDD6FE', // purple-200
      },
      iconTheme: {
        primary: '#7C3AED',
        secondary: '#EDE9FE',
      },
    },
  }
};

/**
 * ToastProvider component
 * 
 * @returns {JSX.Element} Toast provider wrapper
 */
export function ToastProvider() {
  return (
    <div style={{ position: 'fixed', top: -1, left: -1, right: -1, visibility: 'hidden' }}>
      <Toaster 
        {...toastOptions}
        containerStyle={{
          position: 'fixed',
          visibility: 'visible'
        }}
        className="toast-notifications"
      />
    </div>
  );
} 